import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PaginationMetaModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { resetAll } from '../../../../shared/infra/redux/baseActions';
import { ReduxStatus } from '../../../../shared/types';
import { SupportTicketModel } from '../models/SupportTicket.model';
import createSupportTicketComment from './operations/CreateSupportTicketComment';
import createSupportTicketReply from './operations/CreateSupportTicketReply';
import getAdminSupportTicket from './operations/GetAdminSupportTicket';
import getAdminSupportTickets from './operations/GetAdminSupportTickets';
import updateSupportTicketStatus from './operations/UpdateSupportTicketStatus';

export interface AdminSupportState {
  supportTickets: SupportTicketModel[];
  ticketPagination: PaginationMetaModel;
  ticket: SupportTicketModel | null;
  getAdminSupportTicketsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getAdminSupportTicketStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  createSupportTicketComment: {
    status: ReduxStatus;
    error: string | null;
  };
  createSupportTicketReply: {
    status: ReduxStatus;
    error: string | null;
  };
  updateSupportTicketStatus: {
    status: ReduxStatus;
    error: string | null;
  };
}

const initialState: AdminSupportState = {
  supportTickets: [],
  ticket: null,
  ticketPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  getAdminSupportTicketsStatus: {
    status: 'idle',
    error: null,
  },
  getAdminSupportTicketStatus: {
    status: 'idle',
    error: null,
  },
  createSupportTicketComment: {
    status: 'idle',
    error: null,
  },
  createSupportTicketReply: {
    status: 'idle',
    error: null,
  },
  updateSupportTicketStatus: {
    status: 'idle',
    error: null,
  },
};

const adminSupportSlice = createSlice({
  name: 'adminSupportSlice',
  initialState,
  reducers: {
    setSelectedTicket: (
      state,
      action: PayloadAction<SupportTicketModel | null>,
    ) => {
      state.ticket = action.payload;
    },
    clearSelectedTicket: (state) => {
      state.ticket = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAdminSupportTickets.pending, (state) => {
      state.getAdminSupportTicketsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getAdminSupportTickets.fulfilled, (state, { payload }) => {
      state.getAdminSupportTicketsStatus = {
        status: 'success',
        error: null,
      };
      state.supportTickets = payload.model;
      state.ticketPagination = payload.meta;
    });
    builder.addCase(getAdminSupportTickets.rejected, (state, { payload }) => {
      state.getAdminSupportTicketsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch admin support tickets',
      };
    });
    builder.addCase(createSupportTicketComment.pending, (state) => {
      state.createSupportTicketComment = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(createSupportTicketComment.fulfilled, (state) => {
      state.createSupportTicketComment = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(
      createSupportTicketComment.rejected,
      (state, { payload }) => {
        state.createSupportTicketComment = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : 'Error trying to create support ticket comment',
        };
      },
    );
    builder.addCase(createSupportTicketReply.pending, (state) => {
      state.createSupportTicketReply = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(createSupportTicketReply.fulfilled, (state) => {
      state.createSupportTicketReply = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(createSupportTicketReply.rejected, (state, { payload }) => {
      state.createSupportTicketReply = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to create support ticket reply',
      };
    });
    builder.addCase(getAdminSupportTicket.pending, (state) => {
      state.getAdminSupportTicketStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getAdminSupportTicket.fulfilled, (state, { payload }) => {
      state.getAdminSupportTicketStatus = {
        status: 'success',
        error: null,
      };
      state.ticket = payload;
    });
    builder.addCase(getAdminSupportTicket.rejected, (state, { payload }) => {
      state.getAdminSupportTicketStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to get support ticket',
      };
    });
    builder.addCase(updateSupportTicketStatus.pending, (state) => {
      state.updateSupportTicketStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(updateSupportTicketStatus.fulfilled, (state) => {
      state.updateSupportTicketStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(
      updateSupportTicketStatus.rejected,
      (state, { payload }) => {
        state.updateSupportTicketStatus = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : 'Error trying to update support ticket status',
        };
      },
    );
    builder.addCase(resetAll, () => initialState);
  },
});

export const {
  setSelectedTicket,
  clearSelectedTicket,
} = adminSupportSlice.actions;

export default adminSupportSlice.reducer;
